// i18next-extract-mark-ns-start Services
import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { slashNtoBr } from '../../../services/formatting';
import Inspirations from './Inspirations';
import InspirationsMobile from './InspirationsMobile';
import {
  Title, frames, margins, tabletThresholdPixels, mobileThresholdPixels, colors,
} from '../v4/styledComponents';
import withAppContext from '../../../withAppContext';

const Container = styled.div`
  margin: ${margins.m15} ${frames.desktop} 0;
  max-width: 1180px;
  width: 100%;
  @media (max-width: ${tabletThresholdPixels}) {
    margin: ${margins.m14} ${frames.tablet};
    width: calc(100vw - (${frames.tablet}*2));
  }
  @media (max-width: ${mobileThresholdPixels}) {
    margin: 0 ${frames.mobile} ${margins.m8};
    width: calc(100vw - (${frames.mobile}*2));
  }
`;

const RangesContainer = styled.div`
  display: flex;
  width: 100%;
  margin-top: ${margins.m6};
  @media (max-width: ${mobileThresholdPixels}) {
    margin-top: ${margins.m4};
    flex-direction: column;
    gap: ${margins.m4};
  }
`;

const Range = styled.div`
  position: relative;
  width: 100%;
  cursor: pointer;
  ${({ isSelected }) => isSelected
    && `
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 99%;
        background-color: rgba(0, 0, 0, 0.20);
        z-index: 1;
      }
    `}
`;

const Img = styled.img`
  width: 100%;
  object-fit: cover;
  ${({ isSelected }) => isSelected && 'opacity: 0.5;'}
  @media (max-width: ${mobileThresholdPixels}) {
    height: 205px;
    border-radius: 12px;
  }
`;

const Text = styled.p`
  font-size: 28px;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  margin: 0;
  color: ${({ isSelected }) => isSelected ? `${colors.black}` : `${colors.beige100}`};
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
`;

const Subtitle = styled.h3`
  font-size: 16px;
  font-weight: 400;
`;

function OurRanges({
  ranges, preSelectedRange, context: { isMobile }, title, subtitle,
}) {
  const { t } = useTranslation();
  const [selectedRange, setSelectedRange] = useState(preSelectedRange);
  const inspirationsRef = useRef(null);

  const handleRangeClick = (rangeKey) => {
    setSelectedRange(rangeKey);
    if (inspirationsRef.current) {
      const yOffset = -80;
      const y = inspirationsRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const titleToDisplay = title || t('Nos gammes');

  return (
    <Container>
      <Title>{titleToDisplay}</Title>
      {subtitle && <Subtitle>{subtitle}</Subtitle>}
      <RangesContainer>
        {ranges.map((range) => (
          <>
            <Range
              key={range.key}
              onClick={() => handleRangeClick(range.key)}
              isSelected={selectedRange === range.key}
            >
              <Img src={range.image} alt={range.title} isSelected={selectedRange === range.key} />
              <Text isSelected={selectedRange === range.key}>{slashNtoBr(range.title)}</Text>
            </Range>
            {isMobile && selectedRange === range.key && <InspirationsMobile key={range.key} range={selectedRange} />}
          </>
        ))}
      </RangesContainer>
      {!isMobile && (
        <Inspirations ref={inspirationsRef} range={selectedRange} />
      )}
    </Container>
  );
}

OurRanges.propTypes = {
  ranges: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  preSelectedRange: PropTypes.string.isRequired,
  context: PropTypes.shape({
    isMobile: PropTypes.bool,
  }).isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

OurRanges.defaultProps = {
  title: '',
  subtitle: '',
};

export default withAppContext(OurRanges);
